import React from 'react';
import './form.css';

/**
 * Form primitives. The old app had ~29 screens each styling their own
 * inputs inline, which is the main reason no two forms looked alike.
 * A screen now describes its fields and never writes input CSS.
 */

/** Responsive field grid. `columns` is a maximum — it collapses to one
 *  column on narrow screens automatically. */
export function FormGrid({ columns = 2, children }) {
  return <div className={`ui-form-grid ui-form-grid-${columns}`}>{children}</div>;
}

/** A labelled field wrapper: label, control, error and hint. `span`
 *  lets one field take the full width of the grid. */
export function Field({ label, required, error, hint, span, guide, children }) {
  return (
    <div className={`ui-field${span ? ' ui-field-span' : ''}${error ? ' has-error' : ''}`} data-guide={guide}>
      {label && (
        <label className="ui-field-label">
          {label}
          {required && <span className="ui-field-required">*</span>}
        </label>
      )}
      {children}
      {error && <span className="ui-field-error">{error}</span>}
      {!error && hint && <span className="ui-field-hint">{hint}</span>}
    </div>
  );
}

export function Input({ error, ...props }) {
  return <input className={`ui-input${error ? ' has-error' : ''}`} {...props} />;
}

export function Textarea({ error, rows = 3, ...props }) {
  return <textarea className={`ui-input ui-textarea${error ? ' has-error' : ''}`} rows={rows} {...props} />;
}

/** `options` is [{value, label}] or plain strings. */
export function Select({ options = [], placeholder, error, ...props }) {
  return (
    <select className={`ui-input ui-select${error ? ' has-error' : ''}`} {...props}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const value = typeof o === 'object' ? o.value : o;
        const label = typeof o === 'object' ? o.label : o;
        return <option key={String(value)} value={value}>{label}</option>;
      })}
    </select>
  );
}

export function Checkbox({ label, ...props }) {
  return (
    <label className="ui-checkbox">
      <input type="checkbox" {...props} />
      <span>{label}</span>
    </label>
  );
}

/** Mutually exclusive choice rendered as buttons — easier to hit than
 *  radios and matches the table's status filter. */
export function RadioGroup({ options = [], value, onChange, name }) {
  return (
    <div className="ui-radio-group" role="radiogroup">
      {options.map((o) => {
        const v = typeof o === 'object' ? o.value : o;
        const label = typeof o === 'object' ? o.label : o;
        return (
          <button
            key={String(v)}
            type="button"
            name={name}
            className={`ui-radio-btn${value === v ? ' is-active' : ''}`}
            onClick={() => onChange && onChange(v)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Groups related fields under a heading inside a long form. */
export function FormSection({ title, children }) {
  return (
    <fieldset className="ui-form-section">
      {title && <legend className="ui-form-legend">{title}</legend>}
      {children}
    </fieldset>
  );
}
